// PaymentGateway.tsx
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Container,
  Grid,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { useNavigate, useParams } from "react-router-dom";
import CaseApi from "Service/Case.api";
import { formateCaseData } from "./Formatter";
import {
  CaseDataType,
  // ZohoPaymentOptions,
} from "./../../../../../DataTypes/Case.type";
import PaymentApi from "Service/Payment.api";
import { UserInitialStateType } from "DataTypes/Redux";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  setAlertData,
  startLoading,
  stopLoading,
} from "../../../../../Redux/Screen/Action";
import { RouteUrls } from "Constants/RouteUrls";

const PaymentGateway = () => {
  const { caseId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );
  const [caseData, setCaseData] = useState<CaseDataType | null>(null);
  const [orderId, setOrderId] = useState<number | null>(null);
  const [zohoLoaded, setZohoLoaded] = useState(false);
  const [paymentStarted, setPaymentStarted] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);

  useEffect(() => {
    handleOnload();
    const script = document.createElement("script");
    const timestamp = new Date().getTime();
    script.src = `https://static.zohocdn.com/zpay/zpay-js/v1/zpayments.js?t=${timestamp}`; // Add timestamp to bust cache
    script.async = true;
    script.onload = () => {
      setZohoLoaded(true);
    };
    script.onerror = () => {
      dispatch(
        setAlertData({
          alertMessage: "Failed to load Zoho Payments SDK.",
          alertVariant: "error",
          openAlert: true,
        })
      );
    };
    document.body.appendChild(script);

    return () => {
      if ((window as any).ZPayments) {
        delete (window as any).ZPayments;
      }
      const existingScript = document.querySelector(`script[src*="zpayments.js"]`);
      if (existingScript) existingScript.remove();

      const zohoOverlays = document.querySelectorAll('[id^="zpay-"], [class^="zpay-"]');
      zohoOverlays.forEach(el => el.remove());
    };
  }, [caseId]);

  const handleOnload: () => void = async () => {
    const res = await CaseApi.fetchCaseById({ caseId: caseId });
    const orderId = res?.data?.orderTypeId;
    setOrderId(orderId);
    const formattedData = formateCaseData(res?.data);
    setCaseData(formattedData);
  };

  const openZohoWidget = useCallback(
    async (SessionData: {
      sessionId: string;
      amount: number;
      invoiceNumber: string;
      refrenceNumber: string;
      caseId: string;
      user: {
        customerName: string;
        customerEmail: string;
        customerPhone: string;
        customerId: string;
      };
    }) => {
      let instance = null;

      try {
        if (!window.ZPayments) {
          dispatch(
            setAlertData({
              alertMessage: "Zoho SDK not loaded.",
              alertVariant: "error",
              openAlert: true,
            })
          );
          return;
        }
        const config = {
          account_id: process.env.REACT_APP_ZOHO_ACCOUNT_ID,
          domain: process.env.REACT_APP_ZOHO_DOMAIN,
          otherOptions: {
            api_key: process.env.REACT_APP_ZOHO_API_KEY,
            is_test_mode: false
          },
        };

        const options = {
          amount: String(SessionData.amount),
          currency_code: "INR",
          payments_session_id: SessionData.sessionId,
          currency_symbol: "₹",
          business: process.env.REACT_APP_BUSINESS_NAME,
          description: caseData?.caseTypeName,
          invoice_number: SessionData.invoiceNumber,
          reference_number: SessionData.refrenceNumber,
          redirect_url: `${window.location.origin}/payment-status?status=success`,
          cancel_url: `${window.location.origin}/payment-status?status=cancel`,
          address: {
            name: SessionData.user.customerName,
            email: SessionData.user.customerEmail,
            phone: SessionData.user.customerPhone,
          },
        };

        // Create instance WITH config
        instance = new (window.ZPayments as any)(config);

        // Open widget
        const result = await instance.requestPaymentMethod(options);
        if (result?.message === "Payment successful!") {
          dispatch(
            setAlertData({
              alertMessage: result?.message ?? "Payment Successful!",
              alertVariant: "success",
              openAlert: true,
            })
          );
          // If close() throws here, it must not stop navigation or fall into the catch block below
          // (which would show "Payment error" for a payment that actually just succeeded).
          try {
            await instance.close();
          } catch (closeErr) {
            console.error(closeErr, "error closing payment widget after success");
          }
          navigate(`/chat/${orderId}`, { state: { openCaseId: caseId } });
          return;
        } else {
          try {
            await instance.close();
          } catch (closeErr) {
            console.error(closeErr, "error closing payment widget after failure");
          }
          dispatch(
            setAlertData({
              alertMessage: "Payment Failed",
              alertVariant: "error",
              openAlert: true,
            })
          );
        }
      } catch (err: any) {
        // instance can still be null here — if `new ZPayments(config)` itself throws (bad config,
        // CDN hiccup, malformed session response), we land in this catch before instance is ever
        // assigned. Calling .close() on null threw a second, unhandled error that skipped every
        // alert below and left the user with a silent crash instead of a "Payment error" message.
        console.error(err, "error in payment");
        if (err?.code === "widget_closed") {
          dispatch(
            setAlertData({
              alertMessage: "User closed the payment widget.",
              alertVariant: "info",
              openAlert: true,
            })
          );
        } else {
          dispatch(
            setAlertData({
              alertMessage: "Payment error",
              alertVariant: "error",
              openAlert: true,
            })
          );
        }
        if (instance) {
          try {
            await instance.close();
          } catch (closeErr) {
            console.error(closeErr, "error closing payment widget after failure");
          }
        }
        setPaymentFailed(true);
        dispatch(stopLoading());
      }
    },
    [dispatch, navigate, orderId]
  );

  const handleCreateSession = useCallback(async () => {
    dispatch(
      startLoading({ loading: true, loadtext: "Redirecting to Payment..." })
    );
    const payload = {
      caseId: caseId,
    };
    const res = await CaseApi.createPaymentSession(payload);

    if (res?.success) {
      const mapMetadata = (
        metaDataArray: Array<{ key: string; value: any }> = []
      ) => {
        return metaDataArray.reduce((acc: Record<string, any>, item) => {
          acc[item.key] = item?.value;
          return acc;
        }, {} as Record<string, any>);
      };
      const metadata = mapMetadata(res?.data?.payments_session?.meta_data);
      dispatch(stopLoading());
      await openZohoWidget({
        sessionId: res?.data?.payments_session?.payments_session_id,
        amount: Number(res?.data?.payments_session?.amount),
        invoiceNumber: res?.data?.payments_session?.invoice_number,
        refrenceNumber: res?.data?.payments_session?.reference_number,
        caseId: caseId ?? "",
        user: {
          customerName: metadata.customer_name,
          customerEmail: metadata.customer_email,
          customerPhone: metadata.customer_phone,
          customerId: metadata.customer_id,
        },
      });
    } else {
      setPaymentFailed(true);
      dispatch(stopLoading());
    }
  }, [caseId, dispatch, myInfo.id, openZohoWidget]);

  useEffect(() => {
    if (caseData && zohoLoaded && !paymentStarted && !paymentFailed) {
      setPaymentStarted(true);
      handleCreateSession();
    }
  }, [caseData, zohoLoaded, paymentStarted, paymentFailed, handleCreateSession]);

  const handlePayment: () => void = async () => {
    dispatch(
      startLoading({ loading: true, loadtext: "Payment in Progress..." })
    );
    const payload = {
      clientId: myInfo.id,
      caseTypeId: caseData?.caseTypeId,
      orderTypeId: caseData?.orderTypeId,
      description: `Payment for ${caseData?.caseTypeName}`,
      caseId: caseId,
    };
    const res = await PaymentApi.initiatePayment(payload);
    if (res?.success) {
      dispatch(stopLoading());
      dispatch(
        setAlertData({
          alertMessage: "Payment Successfull",
          alertVariant: "success",
          openAlert: true,
        })
      );
      navigate(`../../../${RouteUrls.dashboard}`);
    } else {
      dispatch(stopLoading());
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 10 }}>
      <Card
        sx={{
          boxShadow: 6,
          borderRadius: 4,
          p: 0,
          backgroundColor: "#fefefe",
        }}
      >
        <Box sx={{ p: 3, pb: 2, borderBottom: "1px solid #eee", textAlign: "center", bgcolor: "#f8f9fa" }}>
          <Typography variant="h5" fontWeight={700} color="primary">
            Payment Verification
          </Typography>
        </Box>
        <CardContent sx={{ p: 4, textAlign: "center" }}>
          {!paymentFailed ? (
            <Box>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Redirecting to Secure Payment Gateway...
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Please do not refresh or close this page.
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="h6" color="error" gutterBottom>
                Payment Process Interrupted
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                The payment process could not be completed or was cancelled. Please try again.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                sx={{ py: 1.5, borderRadius: 2, fontSize: "1.1rem", fontWeight: 600 }}
                startIcon={<CreditCardIcon />}
                onClick={() => {
                  setPaymentFailed(false);
                  setPaymentStarted(true);
                  handleCreateSession();
                }}
              >
                Retry Payment
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default PaymentGateway;
